import React, { useState, useEffect } from "react";
import PropertyOverview from "./PropertyTabPages/PropertyOverview";
import OneTimeRentalFinancials from "./PropertyTabPages/OneTimeRentalFinancials";
import PropertyManagementFinancials from "./PropertyTabPages/PropertyManagementFinancials";
import SaleFinancials from "./PropertyTabPages/SaleFinancials";
import PropertyTenantsAndLeases from "./PropertyTabPages/PropertyTenantsAndLeases";
import PropertyMaintenance from "./PropertyTabPages/PropertyMaintenance";
import PropertyFormModal from "../../components/Property/PropertyFormModal";
import PropertyConfirmDelete from "../../components/Property/PropertyConfirmDelete";
import UnlinkTenantModal from "../../components/Property/UnlinkTenantModal";
import ConvertToManagementModal from "../../components/Property/ConvertToManagementModal";
import ConvertToBuildingModal from "../../components/Property/ConvertToBuildingModal";
import UnitFormModal from "../../components/Property/UnitFormModal";
import UnitConfirmDelete from "../../components/Property/UnitConfirmDelete";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: "100vh",
    background: "#F4F5F7",
    padding: "2rem 1.75rem",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  // Spinner
  spinner: {
    display: "inline-block",
    width: 20,
    height: 20,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
  // Cards
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHead: {
    padding: "0.75rem 1.125rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1d23",
  },
  cardBody: { padding: "1rem 1.125rem" },
  // KPI
  kpiCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "1rem 1.125rem",
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
    marginBottom: 6,
  },
  kpiValue: { fontSize: 22, fontWeight: 700, color: "#1a1d23", lineHeight: 1 },
  kpiSub: { fontSize: 12, color: "#9ca3af", marginTop: 5 },
  // Buttons
  btn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnDanger: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #FECACA",
    background: "transparent",
    color: "#991B1B",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 600,
    padding: "6px 16px",
    borderRadius: 8,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  btnBack: {
    fontSize: 12,
    color: "#6b7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    padding: 0,
  },
  // Pills
  pill: (variant) => {
    const map = {
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      teal: { background: "#F0FDFA", color: "#115E59" },
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      red: { background: "#FEF2F2", color: "#991B1B" },
      purple: { background: "#F5F3FF", color: "#5B21B6" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  // Section label
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const typePillVariant = (type) => {
  switch (type) {
    case "one_time_rental":
      return "blue";
    case "property_management":
      return "teal";
    case "sale":
      return "red";
    case "multi_unit_building":
      return "purple";
    default:
      return "gray";
  }
};

const typeLabel = (type) => {
  switch (type) {
    case "one_time_rental":
      return "One-time rental";
    case "property_management":
      return "Property management";
    case "sale":
      return "Sale";
    case "multi_unit_building":
      return "Multi-unit building";
    default:
      return type;
  }
};

const statusPillVariant = (status) => {
  switch (status) {
    case "available":
    case "active":
      return "green";
    case "rented":
    case "pending":
      return "amber";
    case "under_contract":
      return "purple";
    case "sold":
      return "gray";
    case "withdrawn":
    case "inactive":
    case "terminated":
      return "gray";
    default:
      return "gray";
  }
};

const statusLabel = (status) =>
  status ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Unknown";

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const BackIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const InfoIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#92400E"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ── Tab config ────────────────────────────────────────────────────────────────
const ALLOWED_TABS = ["Overview", "Financials", "Tenants and leases", "Maintenance", "Documents"];

// ── Link Tenant Modal ─────────────────────────────────────────────────────────
const ltm = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1060,
    padding: "1rem",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    width: "100%",
    maxWidth: 500,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
    overflow: "hidden",
  },
  head: {
    padding: "1rem 1.25rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  body: { padding: "1.25rem", overflowY: "auto", flex: 1 },
  foot: {
    padding: "0.875rem 1.25rem",
    borderTop: "1px solid #e5e7eb",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },
  btn: {
    fontSize: 12, fontWeight: 500, padding: "6px 16px", borderRadius: 6,
    border: "1px solid #d1d5db", background: "transparent", color: "#374151", cursor: "pointer",
  },
  btnPrimary: {
    fontSize: 12, fontWeight: 600, padding: "6px 18px", borderRadius: 6,
    border: "none", background: "#1E40AF", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 20, lineHeight: 1, color: "#6b7280", padding: "0 2px",
  },
  errorBanner: {
    fontSize: 12, color: "#991B1B", background: "#FEF2F2",
    border: "1px solid #FECACA", borderRadius: 8, padding: "8px 12px", marginBottom: "1rem",
  },
  label: { fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 5 },
  input: {
    width: "100%", padding: "0.5rem 0.75rem", borderRadius: 8,
    border: "1px solid #d1d5db", fontSize: 13, outline: "none", boxSizing: "border-box",
  },
  strip: {
    background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "0.625rem 0.875rem", display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: "0.5rem", marginBottom: "1.25rem",
  },
  stripLabel: { fontSize: 10, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" },
};

const fmtCurr = (n) =>
  new Intl.NumberFormat("en-TT", { style: "currency", currency: "TTD", minimumFractionDigits: 0 }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    timeZone: "America/Port_of_Spain",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

function LinkTenantModal({
  property, tenantsList, tenantsLoading, tenantSearch, onSearchChange,
  form, onFormChange, submitting, error, onSubmit, onClose,
}) {
  const filtered = tenantsList.filter((t) => {
    const name = `${t.personalInfo?.firstName || ""} ${t.personalInfo?.lastName || ""}`.toLowerCase();
    return name.includes(tenantSearch.toLowerCase());
  });
  const selected = tenantsList.find((t) => t._id === form.tenantId);

  return (
    <div style={ltm.overlay} onClick={onClose}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={ltm.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={ltm.head}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1d23" }}>Link tenant</div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              Connect an existing tenant and record lease dates
            </div>
          </div>
          <button style={ltm.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Form — display:contents keeps body + footer as direct flex children */}
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} style={{ display: "contents" }}>
          <div style={ltm.body}>

            {error && <div style={ltm.errorBanner}>{error}</div>}

            {/* Rent / deposit strip */}
            <div style={ltm.strip}>
              <div>
                <div style={ltm.stripLabel}>Monthly rent</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>{fmtCurr(property?.pricing?.rent)}</div>
              </div>
              <div>
                <div style={ltm.stripLabel}>Deposit</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1d23" }}>{fmtCurr(property?.pricing?.deposit)}</div>
              </div>
            </div>

            {/* Tenant picker */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={ltm.label}>Tenant <span style={{ color: "#ef4444" }}>*</span></label>

              {selected ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.5rem 0.75rem", borderRadius: 8, border: "1.5px solid #1E40AF", background: "#EFF6FF" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1E40AF" }}>
                      {selected.personalInfo?.firstName} {selected.personalInfo?.lastName}
                    </div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {selected.personalInfo?.email || selected.personalInfo?.phone || ""}
                    </div>
                  </div>
                  <button type="button" onClick={() => onFormChange((f) => ({ ...f, tenantId: "" }))} style={ltm.closeBtn}>×</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Search by name…"
                    value={tenantSearch}
                    onChange={(e) => onSearchChange(e.target.value)}
                    style={ltm.input}
                  />
                  <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, maxHeight: 160, overflowY: "auto", marginTop: 4 }}>
                    {tenantsLoading ? (
                      <div style={{ padding: "0.75rem", color: "#9ca3af", fontSize: 12, textAlign: "center" }}>Loading…</div>
                    ) : filtered.length === 0 ? (
                      <div style={{ padding: "0.75rem", color: "#9ca3af", fontSize: 12, textAlign: "center" }}>No tenants found</div>
                    ) : filtered.map((t) => (
                      <button
                        key={t._id}
                        type="button"
                        onClick={() => { onFormChange((f) => ({ ...f, tenantId: t._id })); onSearchChange(""); }}
                        style={{ display: "block", width: "100%", textAlign: "left", padding: "0.45rem 0.75rem", background: "none", border: "none", borderBottom: "1px solid #f3f4f6", cursor: "pointer", fontSize: 13 }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f9fafb")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
                      >
                        <div style={{ fontWeight: 500 }}>{t.personalInfo?.firstName} {t.personalInfo?.lastName}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>{t.personalInfo?.email || t.personalInfo?.phone || ""}</div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Lease dates */}
            <div className="re-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem" }}>
              <div>
                <label style={ltm.label}>Lease start <span style={{ color: "#ef4444" }}>*</span></label>
                <input type="date" value={form.leaseStart} onChange={(e) => onFormChange((f) => ({ ...f, leaseStart: e.target.value }))} style={ltm.input} />
              </div>
              <div>
                <label style={ltm.label}>Lease end <span style={{ color: "#ef4444" }}>*</span></label>
                <input type="date" value={form.leaseEnd} onChange={(e) => onFormChange((f) => ({ ...f, leaseEnd: e.target.value }))} style={ltm.input} />
              </div>
            </div>

          </div>

          {/* Footer */}
          <div style={ltm.foot}>
            <button type="button" style={ltm.btn} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" style={{ ...ltm.btnPrimary, opacity: submitting ? 0.6 : 1, cursor: submitting ? "not-allowed" : "pointer" }} disabled={submitting}>
              {submitting ? "Linking…" : "Link tenant"}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
}

// ── Renew Lease Modal ─────────────────────────────────────────────────────────
function RenewLeaseModal({ property, form, onFormChange, submitting, error, onSubmit, onClose }) {
  const currentTenant =
    property?.managementInfo?.currentTenant || property?.occupancy?.currentTenant || null;
  const tenantName =
    currentTenant?.personalInfo
      ? `${currentTenant.personalInfo.firstName || ""} ${currentTenant.personalInfo.lastName || ""}`.trim()
      : property?.placementInfo?.tenantInfo?.tenantName || "the current tenant";
  const prevEnd = property?.managementInfo?.leaseEnd || property?.occupancy?.leaseEnd;

  return (
    <div style={ltm.overlay} onClick={onClose}>
      <div style={ltm.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={ltm.head}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1d23" }}>Renew lease</div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
              Start a new lease term for {tenantName}
            </div>
          </div>
          <button style={ltm.closeBtn} onClick={onClose}>×</button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }} style={{ display: "contents" }}>
          <div style={ltm.body}>
            {error && <div style={ltm.errorBanner}>{error}</div>}

            {/* Rent / deposit strip */}
            <div style={ltm.strip}>
              <div>
                <div style={ltm.stripLabel}>Monthly rent</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#166534" }}>{fmtCurr(property?.pricing?.rent)}</div>
              </div>
              <div>
                <div style={ltm.stripLabel}>Deposit</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1d23" }}>{fmtCurr(property?.pricing?.deposit)}</div>
              </div>
              {prevEnd && (
                <div>
                  <div style={ltm.stripLabel}>Previous lease ended</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1d23" }}>{fmtDate(prevEnd)}</div>
                </div>
              )}
            </div>

            {/* New lease dates */}
            <div className="re-stack-sm" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.875rem", marginBottom: "1.25rem" }}>
              <div>
                <label style={ltm.label}>New lease start <span style={{ color: "#ef4444" }}>*</span></label>
                <input type="date" value={form.leaseStart} onChange={(e) => onFormChange((f) => ({ ...f, leaseStart: e.target.value }))} style={ltm.input} />
              </div>
              <div>
                <label style={ltm.label}>New lease end <span style={{ color: "#ef4444" }}>*</span></label>
                <input type="date" value={form.leaseEnd} onChange={(e) => onFormChange((f) => ({ ...f, leaseEnd: e.target.value }))} style={ltm.input} />
              </div>
            </div>

            {/* Placement fee toggle */}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 13, color: "#374151" }}>
              <input
                type="checkbox"
                checked={form.chargePlacement}
                onChange={(e) => onFormChange((f) => ({ ...f, chargePlacement: e.target.checked }))}
                style={{ width: 15, height: 15, marginTop: 1, accentColor: "#1E40AF", cursor: "pointer" }}
              />
              <span>
                Charge a placement (first-month) commission for this renewal
                <span style={{ display: "block", fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                  Leave unchecked for a standard renewal — only ongoing commission is charged.
                </span>
              </span>
            </label>
          </div>

          {/* Footer */}
          <div style={ltm.foot}>
            <button type="button" style={ltm.btn} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" style={{ ...ltm.btnPrimary, opacity: submitting ? 0.6 : 1, cursor: submitting ? "not-allowed" : "pointer" }} disabled={submitting}>
              {submitting ? "Renewing…" : "Renew lease"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function PropertyShowDetails() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const { id, propertyId, unitId } = useParams();

  const fromBuilding = searchParams.get("fromBuilding");
  const resolvedPropertyId = unitId || propertyId || id;

  const getTabFromQuery = () => {
    const tab = searchParams.get("tab");
    return ALLOWED_TABS.includes(tab) ? tab : "Overview";
  };

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(getTabFromQuery);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [propertyApplication, setPropertyApplication] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [appBaseUrl, setAppBaseUrl] = useState("");
  const [copyStatus, setCopyStatus] = useState("idle");
  const [isEditPropertyModalOpen, setIsEditPropertyModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isUnlinkTenantModalOpen, setIsUnlinkTenantModalOpen] = useState(false);
  const [unlinkingTenant, setUnlinkingTenant] = useState(false);
  const [unlinkTenantError, setUnlinkTenantError] = useState("");
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState("");
  const [isEditUnitModalOpen, setIsEditUnitModalOpen] = useState(false);
  const [isConfirmDeleteUnitModalOpen, setIsConfirmDeleteUnitModalOpen] = useState(false);
  const [isConvertToBuildingModalOpen, setIsConvertToBuildingModalOpen] = useState(false);
  const [convertingToBuilding, setConvertingToBuilding] = useState(false);
  const [convertToBuildingError, setConvertToBuildingError] = useState("");

  // Link-tenant modal
  const [isLinkTenantModalOpen,  setIsLinkTenantModalOpen]  = useState(false);
  const [linkTenantsList,        setLinkTenantsList]        = useState([]);
  const [linkTenantsLoading,     setLinkTenantsLoading]     = useState(false);
  const [linkTenantSearch,       setLinkTenantSearch]       = useState("");
  const [linkTenantForm,         setLinkTenantForm]         = useState({ tenantId: "", leaseStart: "", leaseEnd: "" });
  const [linkingTenant,          setLinkingTenant]          = useState(false);
  const [linkTenantError,        setLinkTenantError]        = useState("");
  const [tenantsRefreshKey,      setTenantsRefreshKey]      = useState(0);

  const [isRenewLeaseModalOpen,  setIsRenewLeaseModalOpen]  = useState(false);
  const [renewLeaseForm,         setRenewLeaseForm]         = useState({ leaseStart: "", leaseEnd: "", chargePlacement: false });
  const [renewingLease,          setRenewingLease]          = useState(false);
  const [renewLeaseError,        setRenewLeaseError]        = useState("");

  // ── Derived ───────────────────────────────────────────────────────────────
  const isUnitView = Boolean(property?.parentProperty) || Boolean(unitId) || Boolean(fromBuilding);

  const isPropertyDeleted = property?.isDeleted === true;

  const parentPropertyId = property?.parentProperty
    ? typeof property.parentProperty === "object"
      ? property.parentProperty?._id
      : property.parentProperty
    : fromBuilding || null;

  const backButtonLink =
    isUnitView && parentPropertyId ? `/properties/${parentPropertyId}/units` : "/properties";

  const applicationLink =
    property &&
    !isPropertyDeleted &&
    (property.type === "one_time_rental" || property.type === "property_management") &&
    appBaseUrl
      ? `${appBaseUrl}/apply/${property._id}`
      : null;

  const hasLinkedTenant =
    (property?.type === "one_time_rental" &&
      (property?.placementInfo?.tenantPlaced || property?.placementInfo?.tenantInfo?.tenantId)) ||
    (property?.type === "property_management" &&
      // managementInfo/occupancy hold the tenant for native management properties;
      // placementInfo holds it for properties converted from a one-time rental.
      (property?.managementInfo?.currentTenant ||
        property?.occupancy?.currentTenant ||
        property?.placementInfo?.tenantPlaced ||
        property?.placementInfo?.tenantInfo?.tenantId));

  const canLinkTenant =
    !isPropertyDeleted &&
    !hasLinkedTenant &&
    (property?.type === "one_time_rental" || property?.type === "property_management");

  // Renew shows for a management property with a linked tenant whose current
  // lease has already ended (end date in the past).
  const currentLeaseEndRaw =
    property?.managementInfo?.leaseEnd || property?.occupancy?.leaseEnd || null;
  const leaseHasEnded =
    currentLeaseEndRaw && new Date(currentLeaseEndRaw).getTime() < Date.now();
  const canRenewLease =
    !isPropertyDeleted &&
    property?.type === "property_management" &&
    hasLinkedTenant &&
    Boolean(leaseHasEnded);

  const tabs = [
    { id: "Overview", label: "Overview", disabled: false },
    { id: "Financials", label: "Financials", disabled: false },
    {
      id: "Tenants and leases",
      label: "Tenants & leases",
      disabled: !["property_management", "one_time_rental", "multi_unit_building"].includes(
        property?.type
      ),
    },
    {
      id: "Maintenance",
      label: "Maintenance",
      disabled: property?.type !== "property_management",
    },
    { id: "Documents", label: "Documents", disabled: false },
  ];

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchProperty = async () => {
      if (!resolvedPropertyId) return;
      setLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.properties.getById(resolvedPropertyId), {
          headers: {
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
            ...getAuthHeaders(),
          },
        });
        const data = await res.json();
        setProperty(data.data);
      } catch (err) {
        console.error("Error fetching property:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [resolvedPropertyId, unitId, getAuthHeaders]);

  useEffect(() => {
    if (typeof window !== "undefined") setAppBaseUrl(window.location.origin);
  }, []);

  useEffect(() => {
    const fetchApplication = async () => {
      if (property?.parentProperty || !property?._id) {
        setPropertyApplication(null);
        return;
      }
      setApplicationLoading(true);
      try {
        const res = await fetch(
          `${API_ENDPOINTS.applications.getAll}?property=${property._id}&limit=1`,
          {
            headers: {
              "Content-Type": "application/json",
              "ngrok-skip-browser-warning": "true",
              ...getAuthHeaders(),
            },
          }
        );
        if (!res.ok) throw new Error("Failed to fetch application");
        const data = await res.json();
        setPropertyApplication(data?.data?.[0] || null);
      } catch (err) {
        console.error("Error fetching application:", err);
        setPropertyApplication(null);
      } finally {
        setApplicationLoading(false);
      }
    };
    fetchApplication();
  }, [property?._id, property?.parentProperty, getAuthHeaders]);

  useEffect(() => {
    setActiveTab(getTabFromQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(backButtonLink);
  };

  const handleEditProperty = (updatedProperty) => {
    setProperty(updatedProperty);
    setIsEditPropertyModalOpen(false);
  };

  const handleEditUnit = (updatedUnit) => {
    setProperty(updatedUnit);
    setIsEditUnitModalOpen(false);
  };

  // ── Link Tenant ───────────────────────────────────────────────────────────
  const openLinkTenantModal = async () => {
    setLinkTenantForm({ tenantId: "", leaseStart: "", leaseEnd: "" });
    setLinkTenantSearch("");
    setLinkTenantError("");
    setIsLinkTenantModalOpen(true);
    setLinkTenantsLoading(true);
    try {
      const res  = await fetch(API_ENDPOINTS.tenants.getAll, {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      const data = await res.json();
      setLinkTenantsList(data.data || []);
    } catch {
      setLinkTenantsList([]);
    } finally {
      setLinkTenantsLoading(false);
    }
  };

  const closeLinkTenantModal = () => {
    setIsLinkTenantModalOpen(false);
    setLinkTenantError("");
  };

  const handleLinkTenantSubmit = async () => {
    if (!linkTenantForm.tenantId)   { setLinkTenantError("Please select a tenant."); return; }
    if (!linkTenantForm.leaseStart) { setLinkTenantError("Lease start date is required."); return; }
    if (!linkTenantForm.leaseEnd)   { setLinkTenantError("Lease end date is required."); return; }

    setLinkingTenant(true);
    setLinkTenantError("");
    try {
      const res  = await fetch(API_ENDPOINTS.properties.linkTenant(property._id), {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          tenantId:        linkTenantForm.tenantId,
          leaseStart:      linkTenantForm.leaseStart,
          leaseEnd:        linkTenantForm.leaseEnd,
          monthlyRent:     Number(property?.pricing?.rent || 0),
          securityDeposit: Number(property?.pricing?.deposit || 0),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to link tenant");
      if (data.data) setProperty(data.data);
      setTenantsRefreshKey((k) => k + 1);
      closeLinkTenantModal();
    } catch (err) {
      setLinkTenantError(err.message || "Something went wrong");
    } finally {
      setLinkingTenant(false);
    }
  };

  // ── Renew lease ─────────────────────────────────────────────────────────────
  const openRenewLeaseModal = () => {
    setRenewLeaseForm({ leaseStart: "", leaseEnd: "", chargePlacement: false });
    setRenewLeaseError("");
    setIsRenewLeaseModalOpen(true);
  };

  const closeRenewLeaseModal = () => {
    setIsRenewLeaseModalOpen(false);
    setRenewLeaseError("");
  };

  const handleRenewLeaseSubmit = async () => {
    if (!renewLeaseForm.leaseStart) { setRenewLeaseError("New lease start date is required."); return; }
    if (!renewLeaseForm.leaseEnd)   { setRenewLeaseError("New lease end date is required."); return; }

    setRenewingLease(true);
    setRenewLeaseError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.renewLease(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          leaseStart:      renewLeaseForm.leaseStart,
          leaseEnd:        renewLeaseForm.leaseEnd,
          chargePlacement: renewLeaseForm.chargePlacement === true,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to renew lease");
      if (data.data) setProperty(data.data);
      setTenantsRefreshKey((k) => k + 1);
      closeRenewLeaseModal();
    } catch (err) {
      setRenewLeaseError(err.message || "Something went wrong");
    } finally {
      setRenewingLease(false);
    }
  };

  const handleDeleteProperty = async (propId) => {
    try {
      const res = await fetch(API_ENDPOINTS.properties.delete(propId), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error("Failed to delete property");
      navigate("/properties");
    } catch (err) {
      console.error("Error deleting property:", err);
    } finally {
      setIsConfirmDeleteModalOpen(false);
    }
  };

  const handleDeleteUnit = async (uId) => {
    try {
      const res = await fetch(API_ENDPOINTS.units.delete(uId), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error("Failed to delete unit");
      const pid =
        typeof property?.parentProperty === "object"
          ? property.parentProperty?._id
          : property?.parentProperty || propertyId;
      navigate(pid ? `/properties/${pid}/units` : "/properties");
    } catch (err) {
      console.error("Error deleting unit:", err);
    } finally {
      setIsConfirmDeleteUnitModalOpen(false);
    }
  };

  const handleUnlinkTenant = async ({ terminationDate, reason, notes, afterUnlink }) => {
    if (!property?._id) return;
    setUnlinkingTenant(true);
    setUnlinkTenantError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.unlinkTenant(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          terminationDate,
          reason,
          notes,
          ...(property.type === "property_management" && afterUnlink ? { afterUnlink } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to unlink tenant");
      setProperty(data?.data?.property || property);
      setIsUnlinkTenantModalOpen(false);
    } catch (err) {
      console.error("Error unlinking tenant:", err);
      setUnlinkTenantError(err.message || "Failed to unlink tenant");
    } finally {
      setUnlinkingTenant(false);
    }
  };

  const handleConvert = async (formData) => {
    if (!property?._id) return;
    setConverting(true);
    setConvertError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.convertToManagement(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg =
          data?.message ||
          (Array.isArray(data?.errors) ? data.errors.map((e) => e.msg).join(", ") : null) ||
          "Failed to convert property";
        throw new Error(msg);
      }
      setProperty(data.data);
      setIsConvertModalOpen(false);
    } catch (err) {
      console.error("Error converting property:", err);
      setConvertError(err.message || "Failed to convert property");
    } finally {
      setConverting(false);
    }
  };

  const handleConvertToBuilding = async (formData) => {
    if (!property?._id) return;
    setConvertingToBuilding(true);
    setConvertToBuildingError("");
    try {
      const res = await fetch(API_ENDPOINTS.properties.convertToBuilding(property._id), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg =
          data?.message ||
          (Array.isArray(data?.errors) ? data.errors.map((e) => e.msg).join(", ") : null) ||
          "Failed to convert property to building";
        throw new Error(msg);
      }
      setIsConvertToBuildingModalOpen(false);
      // Navigate to the newly created building's units page
      navigate(`/properties/${data.data.building._id}/units`);
    } catch (err) {
      console.error("Error converting to building:", err);
      setConvertToBuildingError(err.message || "Failed to convert property to building");
    } finally {
      setConvertingToBuilding(false);
    }
  };

  const handleCopyApplicationLink = async () => {
    if (!applicationLink) return;
    try {
      await navigator.clipboard.writeText(applicationLink);
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    } finally {
      setTimeout(() => setCopyStatus("idle"), 2000);
    }
  };

  // ── Loading / not found ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: "center" }}>
          <Spinner />
          <div style={{ fontSize: 14, color: "#6b7280", marginTop: 8 }}>
            Loading property information…
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div style={{ ...s.page, textAlign: "center", paddingTop: "4rem" }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Property not found</div>
        <button style={s.btn} onClick={() => navigate("/properties")}>
          ← Back to properties
        </button>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page} className="re-detail">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Back navigation ── */}
      <div style={{ marginBottom: "1.25rem" }}>
        <button style={s.btnBack} onClick={handleBackNavigation}>
          <BackIcon />
          {isUnitView && parentPropertyId ? "Back to building" : "Properties"}
        </button>
      </div>

      {/* ── Deleted banner ── */}
      {isPropertyDeleted && (
        <div
          style={{
            ...s.card,
            marginBottom: "1.25rem",
            overflow: "hidden",
            borderColor: "#FDE68A",
          }}
        >
          <div
            style={{
              padding: "0.75rem 1.125rem",
              background: "#FFFBEB",
              display: "flex",
              alignItems: "center",
              gap: 8,
              borderBottom: "1px solid #FDE68A",
            }}
          >
            <InfoIcon />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#92400E" }}>Removed listing</span>
          </div>
          <div style={{ padding: "0.875rem 1.125rem", fontSize: 13, color: "#6b7280" }}>
            This property was removed from your listings. The record is kept so applications,
            tenants, and history can still show the title and address. Editing, tenant unlink, and
            delete are disabled for this record.
          </div>
        </div>
      )}

      {/* ── Page header ── */}
      <div
        className="re-page-header"
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: "1.5rem",
        }}
      >
        {/* Left: title + meta + application link */}
        <div className="re-header-main">
          <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 6 }}>
            {property.title || "Untitled property"}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            <Pill variant={statusPillVariant(property.status)}>{statusLabel(property.status)}</Pill>
            <Pill variant={typePillVariant(property.type)}>{typeLabel(property.type)}</Pill>
            {[property.address?.street, property.address?.city, property.address?.parish]
              .filter(Boolean)
              .join(", ") && (
              <span style={{ fontSize: 12, color: "#6b7280" }}>
                {[property.address?.street, property.address?.city, property.address?.parish]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            )}
            {isUnitView && property.parentProperty?.buildingName && (
              <span style={{ fontSize: 12, color: "#9ca3af" }}>
                Part of {property.parentProperty.buildingName}
              </span>
            )}
          </div>

          {/* Application link */}
          {applicationLink && (
            <div>
              <div style={{ ...s.sectionLabel, marginBottom: 5 }}>Public application link</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <div
                  style={{
                    fontSize: 12,
                    padding: "5px 10px",
                    border: "1px solid #e5e7eb",
                    borderRadius: 6,
                    background: "#f9fafb",
                    color: "#6b7280",
                    flex: "1 1 200px",
                    minWidth: 0,
                    maxWidth: 340,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {applicationLink}
                </div>
                <button style={s.btn} onClick={handleCopyApplicationLink}>
                  {copyStatus === "copied"
                    ? "Copied ✓"
                    : copyStatus === "error"
                      ? "Failed"
                      : "Copy link"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: action buttons */}
        <div className="re-actions" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start" }}>
          {isUnitView && parentPropertyId && (
            <button style={s.btn} onClick={() => navigate(`/properties/${parentPropertyId}/units`)}>
              View building
            </button>
          )}
          {canLinkTenant && (
            <button style={s.btn} onClick={openLinkTenantModal}>
              Link tenant
            </button>
          )}
          {canRenewLease && (
            <button style={s.btn} onClick={openRenewLeaseModal}>
              Renew lease
            </button>
          )}
          {hasLinkedTenant && !isPropertyDeleted && (
            <button
              style={s.btn}
              onClick={() => {
                setUnlinkTenantError("");
                setIsUnlinkTenantModalOpen(true);
              }}
            >
              Unlink tenant
            </button>
          )}
          {!isUnitView &&
            !isPropertyDeleted &&
            (property?.type === "one_time_rental" || property?.type === "property_management") && (
              <button
                style={s.btn}
                onClick={() => {
                  setConvertToBuildingError("");
                  setIsConvertToBuildingModalOpen(true);
                }}
              >
                Convert to building
              </button>
            )}
          {!isPropertyDeleted && (
            <>
              <button
                style={s.btn}
                onClick={() =>
                  isUnitView ? setIsEditUnitModalOpen(true) : setIsEditPropertyModalOpen(true)
                }
              >
                {isUnitView ? "Edit unit" : "Edit property"}
              </button>
              <button
                style={s.btnDanger}
                onClick={() =>
                  isUnitView
                    ? setIsConfirmDeleteUnitModalOpen(true)
                    : setIsConfirmDeleteModalOpen(true)
                }
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div
        style={{
          display: "flex",
          borderBottom: "1px solid #e5e7eb",
          marginBottom: "1.5rem",
          gap: 0,
          overflowX: "auto",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            style={{
              fontSize: 13,
              fontWeight: activeTab === tab.id ? 600 : 400,
              padding: "9px 16px",
              border: "none",
              borderBottom: activeTab === tab.id ? "2px solid #1E40AF" : "2px solid transparent",
              background: "transparent",
              color: tab.disabled ? "#d1d5db" : activeTab === tab.id ? "#1E40AF" : "#6b7280",
              cursor: tab.disabled ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              transition: "color 0.15s ease, border-color 0.15s ease",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div>
        {activeTab === "Overview" && (
          <PropertyOverview
            property={property}
            onConvertClick={
              isPropertyDeleted
                ? undefined
                : () => {
                    setConvertError("");
                    setIsConvertModalOpen(true);
                  }
            }
          />
        )}

        {activeTab === "Financials" && property.type === "one_time_rental" && (
          <OneTimeRentalFinancials property={property} onPropertyUpdated={setProperty} />
        )}
        {activeTab === "Financials" && property.type === "property_management" && (
          <PropertyManagementFinancials property={property} onPropertyUpdated={setProperty} />
        )}
        {activeTab === "Financials" && property.type === "sale" && (
          <SaleFinancials property={property} onPropertyUpdated={setProperty} />
        )}

        {activeTab === "Tenants and leases" && <PropertyTenantsAndLeases property={property} refreshKey={tenantsRefreshKey} />}

        {activeTab === "Maintenance" && property?.type === "property_management" && (
          <PropertyMaintenance property={property} readOnly={isPropertyDeleted} />
        )}

        {activeTab === "Documents" && (
          <div style={{ ...s.card, maxWidth: 680 }}>
            <div style={s.cardHead}>Documents</div>
            <div style={s.cardBody}>
              {applicationLoading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#6b7280",
                    fontSize: 13,
                  }}
                >
                  <Spinner />
                  Checking for linked application…
                </div>
              ) : propertyApplication ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "0.875rem 1rem",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                      Rental application
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Open the submitted application for this property.
                    </div>
                  </div>
                  <button
                    style={s.btnPrimary}
                    onClick={() =>
                      navigate(
                        propertyApplication.viewPath || `/applications/${propertyApplication._id}`
                      )
                    }
                  >
                    View application
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    padding: "0.875rem 1rem",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    fontSize: 13,
                    color: "#9ca3af",
                  }}
                >
                  No application found for this property.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {!isUnitView && (
        <>
          <PropertyFormModal
            show={isEditPropertyModalOpen}
            onHide={() => setIsEditPropertyModalOpen(false)}
            property={property}
            onPropertyUpdated={handleEditProperty}
            mode="edit"
          />
          <PropertyConfirmDelete
            show={isConfirmDeleteModalOpen}
            onHide={() => setIsConfirmDeleteModalOpen(false)}
            property={property}
            onConfirmDelete={handleDeleteProperty}
          />
          <UnlinkTenantModal
            show={isUnlinkTenantModalOpen}
            onHide={() => setIsUnlinkTenantModalOpen(false)}
            property={property}
            onConfirm={handleUnlinkTenant}
            loading={unlinkingTenant}
            error={unlinkTenantError}
          />
          <ConvertToManagementModal
            show={isConvertModalOpen}
            onHide={() => setIsConvertModalOpen(false)}
            property={property}
            onConfirm={handleConvert}
            loading={converting}
            error={convertError}
          />
          <ConvertToBuildingModal
            show={isConvertToBuildingModalOpen}
            property={property}
            onConvert={handleConvertToBuilding}
            onClose={() => {
              setIsConvertToBuildingModalOpen(false);
              setConvertToBuildingError("");
            }}
            loading={convertingToBuilding}
            error={convertToBuildingError}
          />
        </>
      )}

      {/* ── Link Tenant Modal ─────────────────────────────────────────── */}
      {isLinkTenantModalOpen && (
        <LinkTenantModal
          property={property}
          tenantsList={linkTenantsList}
          tenantsLoading={linkTenantsLoading}
          tenantSearch={linkTenantSearch}
          onSearchChange={setLinkTenantSearch}
          form={linkTenantForm}
          onFormChange={setLinkTenantForm}
          submitting={linkingTenant}
          error={linkTenantError}
          onSubmit={handleLinkTenantSubmit}
          onClose={closeLinkTenantModal}
        />
      )}

      {isRenewLeaseModalOpen && (
        <RenewLeaseModal
          property={property}
          form={renewLeaseForm}
          onFormChange={setRenewLeaseForm}
          submitting={renewingLease}
          error={renewLeaseError}
          onSubmit={handleRenewLeaseSubmit}
          onClose={closeRenewLeaseModal}
        />
      )}

      {isUnitView && (
        <>
          <UnitFormModal
            show={isEditUnitModalOpen}
            onHide={() => setIsEditUnitModalOpen(false)}
            unit={property}
            onUnitUpdated={handleEditUnit}
            mode="edit"
          />
          <UnitConfirmDelete
            show={isConfirmDeleteUnitModalOpen}
            onHide={() => setIsConfirmDeleteUnitModalOpen(false)}
            unit={property}
            onConfirmDelete={handleDeleteUnit}
          />
          <UnlinkTenantModal
            show={isUnlinkTenantModalOpen}
            onHide={() => setIsUnlinkTenantModalOpen(false)}
            property={property}
            onConfirm={handleUnlinkTenant}
            loading={unlinkingTenant}
            error={unlinkTenantError}
          />
          <ConvertToManagementModal
            show={isConvertModalOpen}
            onHide={() => setIsConvertModalOpen(false)}
            property={property}
            onConfirm={handleConvert}
            loading={converting}
            error={convertError}
          />
        </>
      )}
    </div>
  );
}

export default PropertyShowDetails;
